// src/services/wechatPublish.ts
// ============================================
// 微信公众号自动发布服务
//
// 功能：
//   1. 获取 access_token（通过 AppID + AppSecret）
//   2. 上传图文素材中的封面图
//   3. 新增永久图文素材
//   4. 发布图文消息
//   5. 查询发布状态
//
// 微信公众号 API 文档：https://developers.weixin.qq.com/doc/offiaccount/Publish/Publish.html
//
// 注意：需要在设置中配置 AppID 和 AppSecret（通过 apiConfig 热插拔）
// 所有请求通过 Vite 代理 /wechat-api 转发，解决 CORS 问题
// ============================================

import { apiConfig } from './apiConfig';
import { WeChatTokenManager } from '../runtime/ContentRuntime/platforms/wechat/token';

/** 微信发布请求参数 */
export interface WeChatPublishRequest {
  /** 文章标题 */
  title: string;
  /** 文章内容（HTML 格式，支持图文混排） */
  content: string;
  /** 封面图 URL（需先上传到微信获取 media_id） */
  thumbMediaId: string;
  /** 作者 */
  author?: string;
  /** 摘要（可选，不传则自动截取） */
  digest?: string;
  /** 原文链接 */
  contentSourceUrl?: string;
  /** 是否显示封面 */
  showCoverPic?: boolean;
  /** 是否开启留言（0=关闭，1=开启） */
  needOpenComment?: number;
}

/** 微信发布结果 */
export interface WeChatPublishResult {
  success: boolean;
  /** 媒体 ID */
  mediaId?: string;
  /** 发布 ID */
  publishId?: string;
  /** 错误信息 */
  error?: string;
  /** 错误码 */
  errcode?: number;
}

/**
 * 简单的 Markdown 转 HTML 转换
 * 支持基础语法：标题（#/##/###）、粗体（**）、斜体（*）、换行
 */
function markdownToHtml(md: string): string {
  if (!md) return '';
  return md
    // 标题转换：### / ## / #
    .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
    // 粗体 **text** → <strong>text</strong>
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // 斜体 *text* → <em>text</em>
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // 换行 → <br>
    .replace(/\n/g, '<br>');
}

/**
 * 微信公众号发布服务
 */
export class WeChatPublishService {
  private logger: Console;

  constructor(logger: Console = console) {
    this.logger = logger;
  }

  /** 获取配置 */
  private getConfig() {
    try {
      const config = apiConfig.get('wechat');
      if (!config || !config.enabled) {
        return null;
      }
      const appId = config.extra?.appId || '';
      const appSecret = config.extra?.appSecret || '';
      // P12 短板4/5/8：AppSecret 由后端 Vault 托管代填，前端仅需 appId 即可发起 token 请求
      if (!appId) {
        return null;
      }
      // 使用 Vite 代理 /wechat-api 解决 CORS 问题
      // 代理配置见 vite.config.ts: /wechat-api -> https://api.weixin.qq.com
      return {
        appId,
        appSecret,
        apiBase: '/wechat-api/cgi-bin', // 通过 Vite 代理转发
      };
    } catch {
      return null;
    }
  }

  /** 检查是否已配置（仅需 AppID，密钥由后端代填） */
  isConfigured(): boolean {
    const config = this.getConfig();
    return !!(config && config.appId);
  }

  /**
   * 获取 access_token（统一使用 WeChatTokenManager 单例，避免多实例并发获取导致 token 互踢）
   */
  async getAccessToken(): Promise<string | null> {
    const config = this.getConfig();
    if (!config) {
      this.logger.error('[WeChatPublish] 微信公众号未配置，请在设置中配置 AppID 和 AppSecret');
      return null;
    }

    try {
      // 通过单例管理器获取 token，传入当前热插拔配置
      return await WeChatTokenManager.getToken({
        appId: config.appId,
        appSecret: config.appSecret,
        proxyUrl: config.apiBase.replace('/cgi-bin', ''),
      });
    } catch (err: any) {
      this.logger.error(`[WeChatPublish] 获取 access_token 异常: ${err?.message}`);
      return null;
    }
  }

  /**
   * 上传封面图（获取 thumb_media_id）
   *
   * @param imageUrl 图片 URL 或 base64
   */
  async uploadThumb(imageUrl: string): Promise<{ success: boolean; mediaId?: string; error?: string }> {
    const token = await this.getAccessToken();
    if (!token) return { success: false, error: 'access_token 获取失败' };

    const config = this.getConfig();
    if (!config) return { success: false, error: '微信公众号未配置' };

    try {
      // 微信上传素材接口（通过 Vite 代理）
      const url = `${config.apiBase}/material/add_material?access_token=${token}&type=image`;

      // 如果是 URL，先下载图片
      let imageBlob: Blob;
      if (imageUrl.startsWith('http')) {
        const imgResp = await fetch(imageUrl);
        imageBlob = await imgResp.blob();
      } else if (imageUrl.startsWith('data:')) {
        // base64
        const base64Data = imageUrl.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteArrays: Uint8Array[] = [];
        for (let i = 0; i < byteCharacters.length; i += 1024) {
          const slice = byteCharacters.slice(i, i + 1024);
          const byteNumbers = new Array(slice.length);
          for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }
        imageBlob = new Blob(byteArrays as BlobPart[], { type: 'image/jpeg' });
      } else {
        return { success: false, error: '不支持的图片格式' };
      }

      const formData = new FormData();
      formData.append('media', imageBlob, 'cover.jpg');

      const resp = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      const data = await resp.json();

      if (data.errcode) {
        return { success: false, error: `${data.errcode}: ${data.errmsg}` };
      }

      return { success: true, mediaId: data.media_id };
    } catch (err: any) {
      return { success: false, error: err?.message || '上传封面失败' };
    }
  }

  /**
   * 新增永久图文素材
   */
  async addNewsArticle(request: WeChatPublishRequest): Promise<WeChatPublishResult> {
    const token = await this.getAccessToken();
    if (!token) {
      return { success: false, error: 'access_token 获取失败' };
    }

    try {
      // 使用草稿箱接口 draft/add（而非 material/add_news），freepublish/submit 才能正确识别 media_id
      const url = `/wechat-api/cgi-bin/draft/add?access_token=${token}`;
      // 提交前将 Markdown 内容转换为 HTML
      const htmlContent = markdownToHtml(request.content);
      const body = {
        articles: [
          {
            title: request.title,
            author: request.author || 'XEAOS',
            digest: request.digest || request.content.slice(0, 120).replace(/<[^>]+>/g, ''),
            content: htmlContent,
            content_source_url: request.contentSourceUrl || '',
            thumb_media_id: request.thumbMediaId,
            show_cover_pic: request.showCoverPic ? 1 : 0,
            need_open_comment: request.needOpenComment ?? 1,
            only_fans_can_comment: 0,
          },
        ],
      };

      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();

      if (data.errcode) {
        return { success: false, error: `${data.errcode}: ${data.errmsg}`, errcode: data.errcode };
      }

      return { success: true, mediaId: data.media_id };
    } catch (err: any) {
      return { success: false, error: err?.message || '新增图文素材失败' };
    }
  }

  /**
   * 发布图文消息（全自动发布）
   */
  async publish(mediaId: string): Promise<WeChatPublishResult> {
    const token = await this.getAccessToken();
    if (!token) {
      return { success: false, error: 'access_token 获取失败' };
    }

    try {
      const url = `/wechat-api/cgi-bin/freepublish/submit?access_token=${token}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ media_id: mediaId }),
      });
      const data = await resp.json();

      if (data.errcode) {
        return { success: false, error: `${data.errcode}: ${data.errmsg}`, errcode: data.errcode };
      }

      return { success: true, publishId: data.publish_id };
    } catch (err: any) {
      return { success: false, error: err?.message || '发布失败' };
    }
  }

  /**
   * 查询发布状态
   */
  async getPublishStatus(publishId: string): Promise<{ status: string; success: boolean; error?: string }> {
    const token = await this.getAccessToken();
    if (!token) return { status: 'failed', success: false, error: 'access_token 获取失败' };

    try {
      const url = `/wechat-api/cgi-bin/freepublish/get?access_token=${token}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publish_id: publishId }),
      });
      const data = await resp.json();

      if (data.errcode) {
        return { status: 'failed', success: false, error: `${data.errcode}: ${data.errmsg}` };
      }

      const article = data.news_item?.[0];
      const state = article?.publish_status || 'unknown';
      return {
        status: state,
        success: state === 'publish_success',
      };
    } catch (err: any) {
      return { status: 'failed', success: false, error: err?.message };
    }
  }

  /**
   * 一键发布（完整流程：上传封面 → 新增图文 → 发布）
   */
  async autoPublish(request: WeChatPublishRequest & { coverUrl?: string }): Promise<WeChatPublishResult> {
    this.logger.info(`[WeChatPublish] 开始自动发布: ${request.title}`);

    // 1. 如果有封面 URL，先上传获取 media_id
    let thumbMediaId = request.thumbMediaId;
    if (!thumbMediaId && request.coverUrl) {
      this.logger.info('[WeChatPublish] 步骤 1/3: 上传封面图...');
      const uploadResult = await this.uploadThumb(request.coverUrl);
      if (!uploadResult.success || !uploadResult.mediaId) {
        return { success: false, error: `上传封面失败: ${uploadResult.error}` };
      }
      thumbMediaId = uploadResult.mediaId;
    }

    // 封面图缺失时降级为仅创建草稿（不执行发布）
    if (!thumbMediaId) {
      this.logger.warn('[WeChatPublish] 未提供封面图，降级为仅创建草稿（跳过发布步骤）');
      const draftResult = await this.addNewsArticle({ ...request, thumbMediaId: '' });
      if (draftResult.success) {
        return {
          success: true,
          mediaId: draftResult.mediaId,
          error: '已创建草稿（缺少封面图，未执行发布，请在公众号后台补充封面后手动发布）',
        };
      }
      return draftResult;
    }

    // 2. 新增永久图文素材
    this.logger.info('[WeChatPublish] 步骤 2/3: 新增图文素材...');
    const newsResult = await this.addNewsArticle({ ...request, thumbMediaId });
    if (!newsResult.success || !newsResult.mediaId) {
      return newsResult;
    }

    // 3. 发布
    this.logger.info('[WeChatPublish] 步骤 3/3: 发布图文消息...');
    const publishResult = await this.publish(newsResult.mediaId);
    if (publishResult.success) {
      this.logger.info(`[WeChatPublish] 发布成功! publish_id: ${publishResult.publishId}`);
    }

    return publishResult;
  }
}

/** 单例 */
let wechatServiceInstance: WeChatPublishService | null = null;

export function getWeChatPublishService(): WeChatPublishService {
  if (!wechatServiceInstance) {
    wechatServiceInstance = new WeChatPublishService();
  }
  return wechatServiceInstance;
}

export default WeChatPublishService;
